import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import { RaInputComponent } from 'src/app/shared/components/ra-input/ra-input.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import {
    RaSelectModel,
    RaSelectOptionModel,
} from 'src/app/shared/components/ra-select/model/ra-select.model';
import { RaSelectComponent } from 'src/app/shared/components/ra-select/ra-select.component';
import { parseLocalizedNumberInput } from 'src/app/shared/utils/number-input.util';
import { normalizeQuoteAmountMode } from 'src/app/shared/utils/quote-money.calculator';

/** Desconto do orçamento — percentual (0 < v <= 100) ou valor fixo. */
type DiscountMode = 'percentage' | 'fixed';

/** O que o modal EMITE ao salvar — sempre no contrato canônico. */
export interface QuoteDiscountFormValue {
    mode: DiscountMode;
    value: number;
}

/**
 * O que o modal RECEBE: o desconto como está no documento. `mode` é `string` de propósito,
 * porque documento criado por app antigo traz `value` no lugar de `fixed` — quem normaliza
 * é o próprio modal.
 */
export interface QuoteDiscountFormInput {
    mode: string;
    value: number;
}

@Component({
    selector: 'modal-quote-discount-form',
    templateUrl: 'modal-quote-discount-form.component.html',
    styleUrl: 'modal-quote-discount-form.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RaModalComponent,
        RaButtonComponent,
        RaInputComponent,
        RaSelectComponent,
        TranslatePipe,
    ],
})
export class ModalQuoteDiscountFormComponent {
    private _translate = inject(TranslateService);

    /** Desconto atual (modo edição) ou `null` (modo novo). */
    public discount = input<QuoteDiscountFormInput | null>(null);
    public loading = input<boolean>(false);
    public currencyCode = input<string | null | undefined>();
    public locale = input<string>('pt-BR');
    public close = output<void>();
    public save = output<QuoteDiscountFormValue>();

    public isEdit = computed(() => !!this.discount());

    public form = new FormGroup({
        mode: new FormControl<DiscountMode>('percentage', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        value: new FormControl<number | null>(null, {
            validators: [Validators.required, Validators.min(0.01)],
        }),
    });

    public modeSelectModel: RaSelectModel;

    constructor() {
        this.modeSelectModel = this._buildModeSelectModel();

        // Modo edição: pré-preenche form e select com o desconto existente.
        effect(() => {
            const d = this.discount();
            // Documento criado por app antigo traz `mode: 'value'`. Sem normalizar, nenhuma
            // opção do select ficava marcada e o modal abria mostrando só o placeholder.
            const mode: DiscountMode = d ? normalizeQuoteAmountMode(d.mode) : 'percentage';
            this.form.patchValue(
                {
                    mode,
                    value: d?.value ?? null,
                },
                { emitEvent: false }
            );
            this._applyValueValidators(mode);
            this._updateModeSelectSelected(mode);
        });
    }

    public get valueLabel(): string {
        return this.isPercentageMode
            ? 'document.edit.quote.discountModal.valuePercentage'
            : 'document.edit.quote.discountModal.valueFixed';
    }

    /** Percentual digita percent (0–100); fixo digita dinheiro na moeda do documento. */
    public get isPercentageMode(): boolean {
        return this.form.controls.mode.value === 'percentage';
    }

    public get valueInputType(): string {
        return this.isPercentageMode ? 'percent' : 'currency';
    }

    public onModeSelect(option: RaSelectOptionModel): void {
        const mode = option.value as DiscountMode;
        this.form.controls.mode.setValue(mode);
        this._applyValueValidators(mode);
        this._updateModeSelectSelected(mode);
        // Dinheiro e percentual não são o mesmo número: trocar de modo zera o valor
        // em vez de reinterpretar "20" como R$ 20,00 ou 20%.
        this.form.controls.value.setValue(null);
    }

    public onSave(): void {
        if (this.form.invalid) return;
        const raw = this.form.getRawValue();
        this.save.emit({
            mode: raw.mode,
            value: parseLocalizedNumberInput(raw.value) ?? 0,
        });
    }

    private _applyValueValidators(mode: DiscountMode): void {
        const validators = [Validators.required, Validators.min(0.01)];
        if (mode === 'percentage') validators.push(Validators.max(100));
        this.form.controls.value.setValidators(validators);
        this.form.controls.value.updateValueAndValidity({ emitEvent: false });
    }

    private _buildModeSelectModel(): RaSelectModel {
        const tr = this._translate;
        const model = new RaSelectModel();
        model.placeholder = tr.instant('document.edit.quote.discountModal.mode');
        model.loading = false;
        model.disabled = false;
        model.listOptions = [
            {
                description: tr.instant('document.edit.quote.modePercentage'),
                value: 'percentage',
                selected: true,
            },
            {
                description: tr.instant('document.edit.quote.modeFixed'),
                value: 'fixed',
                selected: false,
            },
        ];
        return model;
    }

    private _updateModeSelectSelected(mode: DiscountMode): void {
        this.modeSelectModel.listOptions.forEach(opt => {
            opt.selected = opt.value === mode;
        });
    }
}
